import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import { getCollection } from 'api/market-view-api';
import type { CollectionResponse } from 'api/market-view-api';
import {
  createProduct,
  modifyProduct,
} from 'api/products-api';
import type { ProductDto, ProductRequest } from 'api/products-api';
import type { SortDirection, FilterValue } from 'helpers/api/build-query-params';

export type { ProductDto } from 'api/products-api';

export const productsKeys = {
  all: ['products'] as const,
  lists: () => [...productsKeys.all, 'list'] as const,
  list: (params?: ProductsListParams) =>
    [...productsKeys.lists(), params] as const,
};

export interface ProductsListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
}

interface UseProductsListArgs extends ProductsListParams {
  enabled?: boolean;
}

export const useProductsList = ({
  pageSize = 1000,
  pageIndex = 0,
  sortBy = [],
  filters,
  enabled = true,
}: UseProductsListArgs = {}): UseQueryResult<
  CollectionResponse<ProductDto[]>,
  Error
> =>
  useQuery({
    queryKey: productsKeys.list({ pageSize, pageIndex, sortBy, filters }),
    queryFn: async () => {
      const response = await getCollection('products', {
        pageSize,
        pageIndex,
        sortBy,
        filters,
      });

      return response as CollectionResponse<ProductDto[]>;
    },
    enabled,
    placeholderData: (previousData) => previousData,
  });

interface CreateProductVariables {
  userId: string;
  productData: ProductRequest;
}

export const useCreateProduct = (): UseMutationResult<
  { data: ProductDto },
  Error,
  CreateProductVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, productData }: CreateProductVariables) =>
      createProduct({ userId, productData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsKeys.lists() });
    },
  });
};

interface ModifyProductVariables {
  userId: string;
  productId: string | number;
  productData: ProductRequest;
}

export const useModifyProduct = (): UseMutationResult<
  { data: ProductDto },
  Error,
  ModifyProductVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, productId, productData }: ModifyProductVariables) =>
      modifyProduct({ userId, productId, productData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsKeys.lists() });
    },
  });
};
